import { FunctionComponent, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearTimeout, setTimeout } from 'worker-timers';

import { deepEquals } from '@vestico/utils';

import {
  ClientReviewRequestResult,
  ClientReviewResponse,
  DocumentID,
  FetchBoundaries,
  PostDocumentID,
  ProductDocID,
  ProductInfo,
  ProductWidget,
  ReviewProductInfo,
  SupportedMatchInfoHashCategoryDefault,
  WidgetContentEntry,
  userDataCompleted,
} from '@vestico/model';

import { GlobalsContext, UserContext } from '@vestico/shared';

import { MatchingAPIContext, ProductAPIContext, ProductContext, VisitsAPIContext, WidgetAuthContext } from '../../MainContext';
import { ConfigContext, ContentGlobalsContext } from '../../WidgetEventsManager';
import { useSizeRecommendations } from '../../hooks/useSizeRecommendations';
import { AvailableMatchingType, MatchingTypeProps } from '../../model/matchingType';

export interface ContentContextProps extends PropsWithChildren {
  entry: WidgetContentEntry;
}

export interface ContentInteractions {
  loading: boolean;
  loadMore: () => void;
  hasMore: boolean;
  onProductLinkClick: (productInfo: ProductWidget) => void;
}

export interface ContentResponseProps {
  anyResponseHasMatchInfo: boolean | undefined;
  anyResponseHasMatch: boolean | undefined;
  selectedResponse: ClientReviewResponse | undefined;
  onSelectedResponse: (response: ClientReviewResponse) => void;
}

export type ContentResponse = ContentResponseProps & MatchingTypeProps;

export interface ContentProduct {
  products: ProductWidget[] | undefined | null;
}

export const ContentInteractionContext = createContext<ContentInteractions>({} as ContentInteractions);
export const ContentResponsesContext = createContext<ContentResponse>({} as ContentResponse);
export const ContentProductContext = createContext<ContentProduct>({} as ContentProduct);

type ProductMapping = Map<ProductDocID, ProductInfo>;

export const ContentDisplayContext: FunctionComponent<ContentContextProps> = ({ entry, children }) => {
  //
  //  OPEN STATE MGMT //
  //

  const { product: mainProduct, selectedVariantUID: mainProductVariantUID, sizeRecommendationAvailable } = useContext(ProductContext);
  const { contentIsOpen } = useContext(ContentGlobalsContext);
  const { user, uid: userUID } = useContext(UserContext);

  const { stylesConfig } = useContext(GlobalsContext);

  const { authInfo, resetAuth } = useContext(WidgetAuthContext);
  const { productCollection } = useContext(ConfigContext);

  //
  //  FETCH LOGIC //
  //

  const api = useContext(MatchingAPIContext);

  const productAPI = useContext(ProductAPIContext);
  const visitsAPI = useContext(VisitsAPIContext);

  const [reviewResponses, setReviewResponses] = useState<ClientReviewResponse[] | undefined | null>(undefined);
  const [anyResponseHasMatchInfo, setAnyResponseHasMatchInfo] = useState<boolean | undefined>(undefined);
  const [anyResponseHasMatch, setanyResponseHasMatch] = useState<boolean | undefined>(undefined);
  const [lastResponseFetchBoundaries, setLastResponseFetchBoundaries] = useState<FetchBoundaries | undefined>(undefined);
  const [selectedResponse, setSelectedResponse] = useState<ClientReviewResponse | undefined>(undefined);
  const selectedResponseRef = useRef<ClientReviewResponse | undefined>(undefined);
  const [didLoadAllResponses, setDidLoadAllResponses] = useState(false);

  const category = useMemo(() => entry.category ?? SupportedMatchInfoHashCategoryDefault, [entry.category]);

  const onSelectedResponse = useCallback(
    (r: ClientReviewResponse) => {
      setSelectedResponse(r);
      selectedResponseRef.current = r;
    },
    [selectedResponseRef]
  );

  const [loading, setLoading] = useState(false);

  const getProviderDocID = useCallback(() => {
    if (!stylesConfig) {
      return null;
    }

    const { stylesConfig: widgetStylesConfig } = stylesConfig;
    const { providerDocumentID } = widgetStylesConfig;
    if (!providerDocumentID) {
      return null;
    }
    return providerDocumentID;
  }, [stylesConfig]);

  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadAll, setReloadAll] = useState(false);

  const processContentResponseResult = useCallback(
    (responseResult: ClientReviewRequestResult | null, selectedPost: PostDocumentID | undefined) => {
      if (!responseResult) {
        setLoading(false);
        return;
      }

      const {
        reviews,
        anyResponseHasMatchInfo: newAnyResponseHasMatchInfo,
        anyResponseHasMatch: newanyResponseHasMatch,
        fetchBoundaries,
      } = responseResult;
      const allResponses = loadingMore && reviewResponses && !reloadAll ? [...reviewResponses] : [];
      allResponses.push(...reviews);

      const finalAnyResponseHasMatchInfo =
        loadingMore && reviewResponses ? anyResponseHasMatchInfo || newAnyResponseHasMatchInfo : newAnyResponseHasMatchInfo;

      const finalAnyResponseHasMatch = loadingMore && reviewResponses ? anyResponseHasMatch || newanyResponseHasMatch : newanyResponseHasMatch;

      if (fetchBoundaries) {
        setLastResponseFetchBoundaries(fetchBoundaries);
      }

      if (reloadAll && contentIsOpen) {
        const previouslySelectedPostIndex = allResponses.findIndex((response) => response.postDocumentID === selectedPost);
        if (previouslySelectedPostIndex !== -1) {
          onSelectedResponse(allResponses[previouslySelectedPostIndex]);
        } else {
          onSelectedResponse(allResponses[0]);
        }
      }

      setReviewResponses(allResponses);
      setAnyResponseHasMatchInfo(finalAnyResponseHasMatchInfo);
      setanyResponseHasMatch(finalAnyResponseHasMatch);
      setLoading(false);
      setLoadingMore(false);
      setReloadAll(false);
      setDidLoadAllResponses(reviews.length === 0 || !fetchBoundaries);
    },
    [anyResponseHasMatch, anyResponseHasMatchInfo, loadingMore, onSelectedResponse, reloadAll, reviewResponses, contentIsOpen]
  );

  const fetchContentResponses = useCallback(async () => {
    if (!api) {
      console.error('No API object');
      return;
    }

    // Separately block user and userUID.
    if (!user || !userUID) {
      console.debug('no user');
      setLoading(false);
      return;
    }

    const providerDocumentID = getProviderDocID();
    if (!providerDocumentID) {
      console.debug('no provider');
      setLoading(false);
      return;
    }

    if (didLoadAllResponses) {
      console.debug('did load all responses');
      setLoading(false);
      return;
    }

    if (!loading) {
      console.debug('needs to have set loading before');
      return;
    }

    if (reloadAll) {
      setReviewResponses(undefined);
    }

    // fetch
    const responseResult = await api.fetchContentResponses({
      providerDocumentID,
      entryID: entry.id,
      category,
      user,
      authRequestInfo: { authInfo: authInfo, resetAuth },
      selectedPost: undefined,
      reload: reloadAll,
      referenceFetchBoundaries: lastResponseFetchBoundaries,
      product: mainProduct ?? undefined,
      selectedVariantUID: mainProductVariantUID,
      productCollection,
    });

    processContentResponseResult(responseResult, undefined);
  }, [
    api,
    authInfo,
    category,
    productCollection,
    didLoadAllResponses,
    entry.id,
    getProviderDocID,
    lastResponseFetchBoundaries,
    loading,
    mainProduct,
    mainProductVariantUID,
    processContentResponseResult,
    reloadAll,
    resetAuth,
    user,
    userUID,
  ]);

  useEffect(() => {
    if (!loading) {
      return;
    }
    fetchContentResponses();
  }, [loading]);

  useEffect(() => {
    setLoading(true);
    setLoadingMore(false);
    setReloadAll(false);
  }, [authInfo, stylesConfig]);

  useEffect(() => {
    setDidLoadAllResponses(false);
    setLoading(true);
    setLoadingMore(false);
    setReloadAll(true);
  }, [user, userUID]);

  const loadMore = useCallback(() => {
    if (didLoadAllResponses) {
      return;
    }
    setLoading(true);
    setLoadingMore(true);
    setReloadAll(false);
  }, [didLoadAllResponses]);

  //
  //  Clear out all results after timeout. //
  //

  const [refetchAuthInfoTimerID, setRefetchAuthInfoTimerID] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (refetchAuthInfoTimerID !== undefined) {
      clearTimeout(refetchAuthInfoTimerID);
    }

    // force to refetch auth info once it expires
    const delay = 24 * 60 * 60 * 1000;
    const newId = setTimeout(() => {
      setReviewResponses(undefined);
    }, delay);
    setRefetchAuthInfoTimerID(newId);
  }, []);

  //
  //  FETCHING PRODUCT INFO //
  //

  const [productInfos, setProductInfos] = useState<ProductInfo[] | undefined | null>(undefined);
  const fetchedProductInfos = useRef<ProductMapping>(new Map());

  const fetchProducts = useCallback(
    async (products: ReviewProductInfo[]) => {
      if (!productAPI) {
        console.error('No API object');
        return;
      }

      const providerDocumentID = getProviderDocID();
      if (!providerDocumentID) {
        return;
      }

      const productDocIDsToFetch: DocumentID[] = [];
      const alreadyFetchedProducts: ProductInfo[] = [];
      for (const product of products) {
        const { documentID } = product;
        const existingProduct = fetchedProductInfos.current.get(documentID);
        if (!existingProduct) {
          productDocIDsToFetch.push(documentID);
        } else {
          alreadyFetchedProducts.push(existingProduct);
        }
      }

      if (productDocIDsToFetch.length === 0) {
        setProductInfos(alreadyFetchedProducts);
        return;
      }

      const rawProductInfos = await productAPI.fetchRawProducts(productDocIDsToFetch, providerDocumentID, { authInfo: authInfo, resetAuth });

      if (rawProductInfos) {
        // update cache
        for (const rawProductInfo of rawProductInfos) {
          const { product } = rawProductInfo;
          fetchedProductInfos.current.set(product.documentID, rawProductInfo);
        }

        // Process new product infos
        // Keep sorting from database
        // Add the doc ids for comparison as well
        const newProductInfos: ProductInfo[] = [];
        const newProductInfoDocIDs: DocumentID[] = [];
        for (const product of products) {
          const { documentID } = product;
          const existingProduct = fetchedProductInfos.current.get(documentID);
          if (existingProduct) {
            newProductInfos.push(existingProduct);
            newProductInfoDocIDs.push(documentID);
          }
        }

        // only set product if it's still the current one
        const currentPostProductDocIDs = selectedResponseRef.current ? selectedResponseRef.current.products?.map((p) => p.documentID) : undefined;
        if (deepEquals(currentPostProductDocIDs, newProductInfoDocIDs)) {
          setProductInfos(newProductInfos);
        }
      } else {
        setProductInfos(null);
      }
    },
    [productAPI, getProviderDocID, authInfo, resetAuth]
  );

  useEffect(() => {
    if (!contentIsOpen || !reviewResponses || reviewResponses.length === 0 || !selectedResponse) {
      return;
    }

    const { products } = selectedResponse;
    if (products.length === 0) {
      setProductInfos(null);
      return;
    }

    // reset product while loading
    setProductInfos(undefined);
    fetchProducts(products);
  }, [contentIsOpen, fetchProducts, reviewResponses, selectedResponse]);

  const onProductLinkClick = useCallback(
    (product: ProductWidget) => {
      if (visitsAPI && user && user.uid && authInfo && product) {
        console.debug('record showcase product referral');
        visitsAPI.recordVisitEvent('showcaseReferral', [product], user.uid, { authInfo: authInfo, resetAuth });
      }
    },
    [visitsAPI, user, authInfo, resetAuth]
  );

  const contentInteractionContextValue = useMemo(() => {
    return { loadMore, loading, onProductLinkClick, hasMore: !didLoadAllResponses };
  }, [loadMore, loading, onProductLinkClick, didLoadAllResponses]);

  //
  //  MATCH AVAILABILITY //
  //

  const sizeRecAvailableForProduct = useCallback(
    (product: ProductWidget | undefined | null, sizeRecommendationAvailable: boolean | undefined) => {
      return !!user && !!product && userDataCompleted(user, product?.matchingCategory, false, true) && !!sizeRecommendationAvailable;
    },
    [user]
  );

  const sizeRecommendationProductInfo = useMemo(() => productInfos?.[0], [productInfos]);

  const hasSizeRecAvailable = useMemo(
    () =>
      sizeRecAvailableForProduct(mainProduct, sizeRecommendationAvailable) ||
      sizeRecAvailableForProduct(sizeRecommendationProductInfo?.product, sizeRecommendationProductInfo?.sizeRecommendationAvailable),
    [
      mainProduct,
      sizeRecAvailableForProduct,
      sizeRecommendationAvailable,
      sizeRecommendationProductInfo?.product,
      sizeRecommendationProductInfo?.sizeRecommendationAvailable,
    ]
  );
  const [activeMatchingType, setActiveMatchingType] = useState<AvailableMatchingType>('content');
  const onActiveMatchingTypeChange = useCallback((newType: AvailableMatchingType) => setActiveMatchingType(newType), []);

  const { sizeRecommendationResult } = useSizeRecommendations({
    product: sizeRecommendationProductInfo?.product,
    enabled: hasSizeRecAvailable,
    force: activeMatchingType === 'size-rec',
  });

  const contentResponsesContextValue = useMemo(() => {
    const response: ContentResponse = {
      responses: reviewResponses,
      anyResponseHasMatchInfo,
      anyResponseHasMatch,
      selectedResponse,
      onSelectedResponse,
      hasContentMatchAvailable: true,
      hasSizeRecAvailable,
      activeMatchingType,
      onActiveMatchingTypeChange,
      sizeRecommendation: sizeRecommendationResult,
      defaultMatchingType: 'content',
    };
    return response;
  }, [
    reviewResponses,
    anyResponseHasMatchInfo,
    anyResponseHasMatch,
    selectedResponse,
    onSelectedResponse,
    hasSizeRecAvailable,
    activeMatchingType,
    onActiveMatchingTypeChange,
    sizeRecommendationResult,
  ]);

  const contentProductContextValue = useMemo(() => {
    const products = productInfos?.map((productInfo) => {
      return productInfo.product;
    });
    const noProduct = productInfos === null;
    return { products: noProduct ? null : products };
  }, [productInfos]);

  return (
    <ContentInteractionContext.Provider value={contentInteractionContextValue}>
      <ContentResponsesContext.Provider value={contentResponsesContextValue}>
        <ContentProductContext.Provider value={contentProductContextValue}>{children}</ContentProductContext.Provider>
      </ContentResponsesContext.Provider>
    </ContentInteractionContext.Provider>
  );
};
